import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { type FC, useState } from "react";
import { ScoreButtonGroup } from "./scoreButtonGroup.js";

interface AssessmentFeedbackFormProps {
  shouldHideSubmitWhenUnchanged?: boolean;
  shouldAskForScore?: boolean;
  initialValues?: AssessmentFeedbackFormValues;
  onSubmit: (values: AssessmentFeedbackFormValues) => void;
}

export interface AssessmentFeedbackFormValues {
  suggestedScore: number | undefined;
  comments: string;
}

export const AssessmentScoreFeedbackForm: FC<AssessmentFeedbackFormProps> = (
  props,
) => {
  const { initialValues } = props;

  const [isChanged, setIsChanged] = useState(false);
  const [suggestedScore, setSuggestedScore] = useState<number | undefined>(
    initialValues?.suggestedScore,
  );
  const [comments, setComments] = useState<string>(
    initialValues?.comments ?? "",
  );

  const handleSuggestedScoreChange = (newScore: number) => {
    setSuggestedScore(newScore);
    setIsChanged(true);
  };

  const handleCommentsFocus = () => {
    setIsChanged(true);
  };

  const handleCommentsChange = (newComments: string) => {
    setComments(newComments);
    setIsChanged(true);
  };

  const handleSubmit = () => {
    props.onSubmit({ suggestedScore, comments });
  };

  const isSubmitVisible = !props.shouldHideSubmitWhenUnchanged || isChanged;
  const isSubmitDisabled =
    suggestedScore === undefined && comments.length === 0 && !isChanged;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {props.shouldAskForScore ? (
          <div className="space-y-1">
            <Label size={Size.SMALL}>
              What score should this interview have received?
            </Label>
            <ScoreButtonGroup
              score={suggestedScore}
              onChange={handleSuggestedScoreChange}
            />
          </div>
        ) : null}

        <Textarea
          label="Comments (optional)"
          value={comments}
          onFocus={handleCommentsFocus}
          onChange={handleCommentsChange}
          placeholder={
            props.shouldAskForScore
              ? "Why is this the right score?"
              : "Anything else you'd like to share?"
          }
          height="5rem"
        />

        <p className="text-xs text-muted-foreground">
          Your feedback will be used to generate recommendations for improving
          AI interview questions and grading criteria.
        </p>
      </div>

      <div className="flex justify-end space-x-2">
        {isSubmitVisible ? (
          <Button
            variant={ButtonVariant.Primary}
            size={Size.MEDIUM}
            onClick={handleSubmit}
            isDisabled={isSubmitDisabled}
          >
            Save
          </Button>
        ) : null}
      </div>
    </div>
  );
};
